import { useEffect, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useDebouncedValue from "hooks/useDebouncedValue";
import { Option } from "./AsyncMultiSelect";

type Props = {
  label: string;
  value: Option | null;
  onChange: (value: Option | null) => void;
  fetchOptions: (search: string) => Promise<Option[]>;
  required?: boolean;
};

const DEBOUNCE_MS = 400;

function AsyncSingleSelect({ label, value, onChange, fetchOptions, required }: Props) {
  const [inputValue, setInputValue] = useState("");
  const debouncedInput = useDebouncedValue(inputValue, DEBOUNCE_MS);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOptions(debouncedInput)
      .then((results) => {
        if (!cancelled) setOptions(results);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={loading}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      getOptionLabel={(option) => option.label}
      filterOptions={(opts) => opts}
      onChange={(_e, newValue) => onChange(newValue)}
      onInputChange={(_e, newInput) => setInputValue(newInput)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default AsyncSingleSelect;
